<template>
  <div class="thumbnail" ref="el">
    <img v-show="isLoaded" ref="img" />
    <div class="loader_container" v-if="!isLoaded">
      <LoadingSpinner :color="'grey'" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch, onBeforeUnmount } from "vue";
import { HTTP } from "@/util";
import { token } from "@/init";
import { LoadingSpinner } from "@/components";
import { useIntersectionObserver } from "@vueuse/core";

const props = defineProps({
  pageId: {
    type: String,
    required: false,
  },
});

const hasBeenVisible = ref(false);
const el = ref(null);
const { stop } = useIntersectionObserver(el, ([{ isIntersecting }]) => {
  if (isIntersecting) {
    hasBeenVisible.value = true;
    stop();
  }
});

const isLoaded = ref(false);

const img = ref();
let abortController: AbortController | null = null;
let objectUrl: string | null = null;

const loadImg = async () => {
  if (abortController) {
    abortController.abort();
  }

  abortController = new AbortController();

  const headers = new Map<string, string>();
  headers.set("Authorization", `Bearer ${token.value!}`);

  try {
    const res = await HTTP.get(
      `/backend/thumbnails/page/${props.pageId}`,
      headers,
      { signal: abortController.signal },
    );

    const blob = await res.blob();

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }

    objectUrl = URL.createObjectURL(blob);
    if (img.value) {
      img.value.onload = null;
      img.value.onload = () => {
        isLoaded.value = true;
      };
      img.value.src = objectUrl;
    }
  } catch (e) {
    //fallthrough
  }
};

watch(
  () => props.pageId,
  (newPageId, oldPageId) => {
    if (newPageId && newPageId !== oldPageId && hasBeenVisible.value === true) {
      loadImg();
    }
  },
  { immediate: true },
);

watch(
  hasBeenVisible,
  (newVisible, oldVisible) => {
    if (newVisible && !oldVisible) {
      loadImg();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (abortController) {
    abortController.abort();
  }
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
  }
});
</script>

<style scoped>
.loader_container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

img {
  max-width: 100%;
  max-height: 100%;
}
</style>
